/**
 * The photos on one curated product.
 *
 * Order is the whole point of this screen, not decoration: the first image is what every
 * shopper sees on a card and in search results, and every vendor selling this product
 * shows it. So the first slot is labelled rather than left to be inferred, and adding a
 * photo appends — nobody should take over the shelf image by accident.
 *
 * Reordering is arrows rather than drag-and-drop. A four-to-eight item list does not need
 * a drag library, and arrows work on a trackpad, a touchscreen and a keyboard without any
 * of the hit-target problems drag has.
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthImage } from '@/components/auth-image';
import {
  use_add_catalog_image,
  use_delete_catalog_image,
  use_reorder_catalog_images,
} from '@/features/catalog-products/hooks';
import type { CatalogProduct } from '@/features/catalog-products/types';

export function CatalogGalleryEditor({ product }: { product: CatalogProduct }) {
  const file_input = useRef<HTMLInputElement>(null);
  const [error, set_error] = useState<string | null>(null);
  const [busy_id, set_busy_id] = useState<string | null>(null);

  const add_m = use_add_catalog_image();
  const remove_m = use_delete_catalog_image();
  const reorder_m = use_reorder_catalog_images();

  /*
   * The gallery is held locally and refreshed from each mutation's response.
   *
   * `product` is a snapshot taken when the row was clicked, so invalidating the list
   * refetches it without this component ever seeing the new version — uploads appeared to
   * do nothing at all. Every one of these endpoints returns the updated product, so the
   * response is the source of truth here rather than a re-read.
   */
  const [images, set_images] = useState(product.images ?? []);
  useEffect(() => {
    set_images(product.images ?? []);
  }, [product.id, product.images]);

  const busy = add_m.isPending || remove_m.isPending || reorder_m.isPending;

  /** Upload every chosen file, in the order they were picked. */
  async function add_files(files: FileList) {
    set_error(null);
    /*
     * Sequential, not Promise.all. Order matters here and the server appends, so
     * concurrent uploads would land in a nondeterministic order — and the first slot is
     * the one that matters most.
     */
    for (const file of Array.from(files)) {
      try {
        const updated = await add_m.mutateAsync({ id: product.id, file });
        set_images(updated.images ?? []);
      } catch (e) {
        set_error(e instanceof Error ? e.message : `Could not upload ${file.name}.`);
        break;
      }
    }
    if (file_input.current) file_input.current.value = '';
  }

  async function move(index: number, delta: number) {
    const next = [...images];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    set_error(null);
    // Optimistic, because an arrow tap should move the tile immediately.
    set_images(next);
    try {
      const updated = await reorder_m.mutateAsync({ id: product.id, image_ids: next.map((i) => i.id) });
      set_images(updated.images ?? next);
    } catch (e) {
      set_images(images);
      set_error(e instanceof Error ? e.message : 'Could not reorder the photos.');
    }
  }

  async function remove(image_id: string) {
    set_error(null);
    set_busy_id(image_id);
    try {
      const updated = await remove_m.mutateAsync({ id: product.id, image_id });
      set_images(updated.images ?? []);
    } catch (e) {
      set_error(e instanceof Error ? e.message : 'Could not remove the photo.');
    } finally {
      set_busy_id(null);
    }
  }

  return (
    <div className="grid gap-2" data-testid="catalog-gallery">
      {images.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="catalog-gallery-empty">
          No photos yet. The first one you add becomes the picture shoppers see on the card.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-3" data-testid="catalog-gallery-list">
          {images.map((img, i) => (
            <li
              key={img.id}
              className="w-28 rounded-card border border-border p-1.5"
              data-testid={`catalog-gallery-item-${img.id}`}
            >
              <AuthImage
                path={`${img.path}?variant=thumb`}
                alt={`${product.name} photo ${i + 1}`}
                className="h-24 w-full overflow-hidden rounded-button object-cover"
              />
              {i === 0 ? (
                /* Named, because "first" is a commercial decision and not obvious. */
                <p
                  className="mt-1 text-center text-micro font-semibold text-primary"
                  data-testid="catalog-gallery-primary"
                >
                  Shown on cards
                </p>
              ) : (
                <p className="mt-1 text-center text-micro text-muted-foreground">Photo {i + 1}</p>
              )}
              <div className="mt-1 flex items-center justify-center gap-0.5">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-1.5"
                  disabled={busy || i === 0}
                  aria-label="Move earlier"
                  data-testid={`catalog-gallery-left-${img.id}`}
                  onClick={() => move(i, -1)}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-1.5"
                  disabled={busy || i === images.length - 1}
                  aria-label="Move later"
                  data-testid={`catalog-gallery-right-${img.id}`}
                  onClick={() => move(i, 1)}
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-1.5"
                  disabled={busy}
                  aria-label="Remove photo"
                  data-testid={`catalog-gallery-remove-${img.id}`}
                  onClick={() => remove(img.id)}
                >
                  {busy_id === img.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  )}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input
          id="c-images"
          ref={file_input}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          data-testid="catalog-gallery-input"
          className="cursor-pointer text-micro text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-control file:border file:border-border file:bg-secondary file:px-3 file:py-1.5 file:font-heading file:text-data file:font-semibold file:text-secondary-foreground hover:file:bg-muted"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) void add_files(e.target.files);
          }}
        />
        {add_m.isPending && (
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="catalog-gallery-busy">
            <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
          </span>
        )}
        {images.length === 0 && !add_m.isPending && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <ImagePlus className="h-3.5 w-3.5" /> Pick several at once
          </span>
        )}
      </div>

      {error && (
        <p className="text-sm text-destructive" role="alert" data-testid="catalog-gallery-error">
          {error}
        </p>
      )}
    </div>
  );
}
